import { useEffect, useRef, useState } from "react";
import { filetoUrl } from "./Functions/FileToUrl";
import { URLtoFile } from "./Functions/URLtoFile";
import { useDispatch, useSelector } from "react-redux";
import { uploadWaterMarkLogo } from "../../store/slices/GetStarted/getStartedSlice";

export const useWaterMark = ({ sampleUrl, open, close, positionData }) => {
  const dispatch = useDispatch();
  const { userProfileData } = useSelector((state) => state.userData);

  const canvasRef = useRef();
  const [opacity, setOpacity] = useState(100);
  const [watermarkImage, setWatermarkImage] = useState("");
  const [tempWatermark, setTempWatermark] = useState(null);
  const [fileName, setFileName] = useState("");
  const [sampleImage, setSampleImage] = useState(sampleUrl);
  const [option, setOption] = useState(positionData[0]);
  const [error, setError] = useState("");

  const { waterMarkStatus } = useSelector((state) => state.getStarted);

  useEffect(() => {
    if (canvasRef?.current && userProfileData?.water_mark_url) {
      setWatermarkImage(userProfileData?.water_mark_url);
      setOpacity(parseInt(localStorage.getItem("watermark_opacity")));
      setFileName(userProfileData?.file_name);
      setOption(
        positionData?.filter(
          (item) => item?.id == userProfileData?.preference?.watermark_type
        )?.[0]
      );

      setTimeout(() => {
        DrawImage(
          sampleUrl,
          userProfileData?.water_mark_url,
          positionData?.filter(
            (item) => item.id == userProfileData?.preference?.watermark_type
          )?.[0]?.value,
          0.01 * parseInt(localStorage.getItem("watermark_opacity"))
        );
      }, 200);
    } else if (open) {
      DrawImage(
        sampleUrl,
        userProfileData?.water_mark_url,
        option?.value,
        0.01 * opacity
      );
    }
  }, [open, canvasRef?.current]);

  const handleChangeImage = async (e) => {
    const allowedExtensions = ["png"];
    const file = e.target.files[0];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (allowedExtensions?.includes(fileExtension)) {
      setFileName(e?.target?.files[0]?.name);
      setWatermarkImage(URL.createObjectURL(e.target.files[0]));
      setTempWatermark(e.target.files[0]);
      await DrawImage(
        sampleUrl,
        URL.createObjectURL(e.target.files[0]),
        option?.value,
        0.01 * opacity
      );
    } else {
      setError("The file must be a .png file.");
      setTimeout(() => {
        setError("");
      }, 4000);
    }
  };

  const handleChangeOpacity = (e) => {
    const newOpacity = e.target.value;
    setOpacity(newOpacity);
    localStorage.setItem("watermark_opacity", newOpacity);
    requestAnimationFrame(() => {
      DrawImage(sampleUrl, watermarkImage, option?.value, 0.01 * newOpacity);
    });
  };

  const handleChangePosition = (e) => {
    setOption(e);
    DrawImage(sampleUrl, watermarkImage, e.value, 0.01 * opacity);
  };

  const applyOpacity = (fileUrl, opacity, resCallback) => {
    if (fileUrl) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = fileUrl;
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.globalAlpha = opacity;
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
        const resultDataURL = canvas.toDataURL("image/png");
        let imgFile = URLtoFile(resultDataURL, "image/png");
        resCallback(resultDataURL, imgFile);
      };
    }
  };

  const DrawImage = (sampleImage, watermarkImage, option, opacityValue) => {
    const canvas = canvasRef?.current;

    if (!canvas) {
      throw new Error("No 2d context");
    }
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.crossOrigin = "anonymous";
    const watermark = new Image();
    watermark.crossOrigin = "anonymous";
    img.src = sampleImage;
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear the canvas before drawing
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
      drawWatermark();
    };

    const drawWatermark = () => {
      if (watermarkImage) {
        watermark.src = watermarkImage;

        watermark.onload = () => {
          const waterMarkWidth = 30;
          let watermarkHeight =
            waterMarkWidth * (watermark.naturalHeight / watermark.naturalWidth);

          ctx.filter = "grayscale(100%)";
          ctx.globalAlpha = opacityValue;

          if (option === "3-dimension") {
            for (let index = 0; index < 3; index++) {
              let yPosition = index * 100 + 10;
              ctx.drawImage(
                watermark,
                10,
                yPosition,
                waterMarkWidth,
                watermarkHeight
              );
              ctx.drawImage(
                watermark,
                80 + 10,
                yPosition,
                waterMarkWidth,
                watermarkHeight
              );
              ctx.drawImage(
                watermark,
                160,
                yPosition,
                waterMarkWidth,
                watermarkHeight
              );
            }
          } else if (option === "center") {
            let x = Math.floor(canvas.width / 2 - waterMarkWidth / 2);
            let y = Math.floor(canvas.height / 2 - watermarkHeight / 2);
            ctx.drawImage(watermark, x, y, waterMarkWidth, watermarkHeight);
          } else if (option === "right-corner") {
            ctx.drawImage(
              watermark,
              canvas.width - 50,
              canvas.height - 70,
              waterMarkWidth,
              watermarkHeight
            );
          }

          ctx.filter = "none";
        };
      }
    };
  };

  const handleSave = async () => {
    applyOpacity(watermarkImage, 0.01 * opacity, async function (url, file) {
      setTempWatermark(url);
      const blackAndWhiteFile = await convertToBlackAndWhite(file);
      let formData = new FormData();
      formData.append("image", blackAndWhiteFile);
      formData.append("watermark_type", option?.id);
      let resultAction = await dispatch(
        uploadWaterMarkLogo({ formData, type: option?.id })
      );
      if (uploadWaterMarkLogo.fulfilled.match(resultAction)) {
        setFileName("");
        close();
      }
    });
  };

  //for converting watermark to black and white.
  const convertToBlackAndWhite = async (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        ctx.filter = "grayscale(100%)";
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const blackAndWhiteFile = new File([blob], fileName.toString(), {
            type: "image/png",
          });
          resolve(blackAndWhiteFile);
        }, "image/png");
      };
    });
  };

  return {
    canvasRef,
    sampleImage,
    fileName,
    opacity,
    watermarkImage,
    tempWatermark,
    option,
    waterMarkStatus,
    setOpacity,
    setWatermarkImage,
    handleChangeImage,
    handleChangeOpacity,
    handleChangePosition,
    handleSave,
    error,
  };
};
